import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { adminLocationsQuery } from '@/queries';

/**
 * Record an adjustment by hand. Task T076, FR-108.
 *
 * The reasons offered here are the ones a person is allowed to type. `received`,
 * `picked` and `counted` are absent on purpose: stock arrives through a goods
 * receipt so it carries a cost and a purchase order, leaves through a job or a
 * reservation, and is corrected by an approved stocktake. The server refuses
 * them regardless (R-92); the form simply does not pretend they are options.
 *
 * `written_off` and `corrected` need the inventory delete right. The form shows
 * them and lets the server answer, because a 403 that names the missing
 * permission is more useful than an option that silently is not there.
 */

const REASONS = [
  ['wasted', 'Wasted', 'Spoiled or damaged in the unit'],
  ['returned', 'Returned', 'Came back onto the shelf'],
  ['transferred', 'Transferred', 'Moved to or from another location'],
  ['written_off', 'Written off', 'Gone, and not coming back. Needs the delete right'],
  ['corrected', 'Correction', 'Fixes an earlier movement. Needs the delete right'],
] as const;

export function RecordMovement({ itemId, uom }: { itemId: string; uom: string }) {
  const qc = useQueryClient();
  const { locations } = useSuspenseQuery(adminLocationsQuery).data;

  const [open, setOpen] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState<string>('wasted');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/stock/movements', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          item_id: itemId,
          location_id: locationId,
          qty: Number(qty),
          reason,
          note: note.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      setDone(`Recorded. The history below is the record; it is never edited.`);
      setQty('');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
    onError: (e: Error) => {
      setDone(null);
      setError(e.message);
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-[10px] border border-line px-3 py-2 text-[14px]"
      >
        Record an adjustment
      </button>
    );
  }

  const valid = locationId && qty.trim() !== '' && Number(qty) !== 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) submit.mutate();
      }}
      className="rounded-[10px] border border-line bg-panel p-4"
    >
      <p className="mb-3 text-[15px] font-semibold">Record an adjustment</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-[13px]">
          Location
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="mt-1 h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
          >
            <option value="">Choose a location</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id} disabled={Boolean(l.locked_by_take_id)}>
                {l.code} {l.label}
                {l.locked_by_take_id ? ' — being counted' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="text-[13px]">
          Quantity in {uom}, negative to take away
          <input
            type="number"
            step="any"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="-3"
            className="mt-1 h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
          />
        </label>
      </div>

      <fieldset className="mt-3">
        <legend className="text-[13px]">Reason</legend>
        <div className="mt-1 grid gap-1">
          {REASONS.map(([value, label, blurb]) => (
            <label key={value} className="flex items-baseline gap-2 text-[14px]">
              <input
                type="radio"
                name="reason"
                value={value}
                checked={reason === value}
                onChange={() => setReason(value)}
              />
              <span>
                {label} <span className="text-[13px] text-muted">— {blurb}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="mt-3 block text-[13px]">
        Note
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What happened, in a few words"
          className="mt-1 h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
        />
      </label>

      {error && (
        <p className="mt-3 rounded-[10px] border border-line p-3 text-[14px]">{error}</p>
      )}
      {done && <p className="mt-3 text-[14px] text-muted">{done}</p>}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="submit"
          disabled={!valid || submit.isPending}
          className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
        >
          {submit.isPending ? 'Recording…' : 'Record it'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[14px] text-muted"
        >
          Close
        </button>
      </div>
    </form>
  );
}
